import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  getEventRegistrationCounts,
  getMyEventRegistrationIds,
} from "@/lib/event-registration.functions";
import { listPublishedEvents } from "@/lib/events.functions";

type SiteEvent = Awaited<ReturnType<typeof listPublishedEvents>>[number];

export function useEventRegistrationState(events: SiteEvent[]) {
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setAuthed(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setAuthed(!!s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const eventIds = events.map((e) => e.id);

  const countsQ = useQuery({
    queryKey: ["event-registration-counts", eventIds],
    queryFn: () => getEventRegistrationCounts({ data: { eventIds } }),
    enabled: eventIds.length > 0,
  });

  const myIdsQ = useQuery({
    queryKey: ["my-event-registration-ids"],
    queryFn: () => getMyEventRegistrationIds(),
    enabled: authed,
  });

  const countMap = useMemo(
    () => new Map((countsQ.data ?? []).map((c) => [c.eventId, c])),
    [countsQ.data],
  );
  const registeredIds = useMemo(() => new Set(myIdsQ.data ?? []), [myIdsQ.data]);

  return { countMap, registeredIds, authed };
}
